import { useState, useEffect, useCallback } from "react"
import usePaginatedListFetch from "../../utils/usePaginatedListFetch.js"
import { mergeLoadedPage } from "../../utils/mergeLoadedPage.js"
import ListPagination from "../common/ListPagination.js"
import SearchSubmitInput from "../common/SearchSubmitInput.js"
import { useNavigate } from "react-router-dom"
import { shipmentService } from "../../services/shipmentService"
import { locationHubService } from "../../services/locationHubService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from "../common/Alert"
import { Package, Plus, FileDown, BarChart3, X, QrCode, ChevronDown } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import ListPageHeader from '../common/ListPageHeader.js';
import Button from '../common/Button.js'
import RowActionsMenu from '../common/RowActionsMenu.js'
import { showShipmentNetworkScopeTabs, hasShipmentCrossingAccess } from "../../utils/networkMode.js"
import agentPartnershipService from "../../services/agentPartnershipService.js"
import { printPdfBlob, parseBlobErrorMessage } from "../../utils/printPdfBlob.js"
import { openHtmlPrintWindow } from "../../utils/openHtmlPrintWindow.js"
import { useTranslation } from 'react-i18next';
import { listIdLinkMobileClass, listPageWrapperClass, listScopeTabClass, listScopeTabSmClass } from '../common/uiClasses.js';
import {
  formatHubBranchLabel,
  filterShipmentLocations,
} from '../../utils/destinationType.js';
import { formatIndianDate, formatIndianTime, formatTimeAgo } from '../../utils/localDate.js';
import {
  displayShipmentStatus,
  isCancelPending,
  isCancelledShipment,
} from '../../utils/shipmentCancel.js';
import CancelShipmentModal from './CancelShipmentModal.js';

const canEditShipmentRow = (shipment, user) => {
  if (isCancelledShipment(shipment) || isCancelPending(shipment)) return false
  const role = user?.role
  if (role === 'System Admin' || role === 'Admin') return true
  if (shipment?.can_edit != null) return Boolean(shipment.can_edit)
  if (shipment?.on_ml_sheet || shipment?.ml_sheet_id) return false
  return role === 'Accountant' || role === 'Hub Admin'
}

const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const formatListQuantity = (shipment) => {
  const qty = shipment?.no_of_articles
  if (qty == null || qty === "") return "—"
  return String(qty)
}

const formatListWeight = (shipment) => {
  const raw = shipment?.weight
  if (raw == null || raw === "") return "—"
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return "—"
  return Number.isInteger(n) ? `${n} kg` : `${n.toFixed(2)} kg`
}

const ShipmentList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { user } = useAuth()
  const [shipments, setShipments] = useState([])
  const [loading, setLoading] = useState(true)
  const [bulkQrDownloading, setBulkQrDownloading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [receiptPrintCountTotal, setReceiptPrintCountTotal] = useState(null)
  const todayStr = getLocalDateString()
  const canSeeReceiptPrintCount = user?.role === "Admin" || user?.role === "Accountant"
  const [filters, setFilters] = useState({
    status: "",
    search: "",
    from_date: todayStr,
    to_date: todayStr,
    mode_of_payment: [],
    user_id: "",
    branch: "",
    sender_location: [],
    receiver_location: [],
    sender_location_id: [],
    receiver_location_id: [],
  })
  const [users, setUsers] = useState([])
  const [openMenuId, setOpenMenuId] = useState(null)
  const [shipmentLocations, setShipmentLocations] = useState([])
  const [senderLocationSearch, setSenderLocationSearch] = useState("")
  const [receiverLocationSearch, setReceiverLocationSearch] = useState("")
  const [senderLocationDropdownOpen, setSenderLocationDropdownOpen] = useState(false)
  const [receiverLocationDropdownOpen, setReceiverLocationDropdownOpen] = useState(false)
  const [bookedByLocation, setBookedByLocation] = useState(null)
  const [bookedByLocationLoading, setBookedByLocationLoading] = useState(false)
  const [bookedByLocationOpen, setBookedByLocationOpen] = useState(false)
  const [breakdownTab, setBreakdownTab] = useState("location")
  const [paymentModeDropdownOpen, setPaymentModeDropdownOpen] = useState(false)
  const [listScope, setListScope] = useState("all")
  const [partnerCompanyId, setPartnerCompanyId] = useState("")
  const [partnerCompanies, setPartnerCompanies] = useState([])
  const [cancelModal, setCancelModal] = useState({ open: false, mode: 'request', shipment: null })
  const [cancelSubmitting, setCancelSubmitting] = useState(false)
  const showNetworkTabs = showShipmentNetworkScopeTabs(user)

  useEffect(() => {
    if (!showNetworkTabs && listScope !== "all") {
      setListScope("all")
      setPartnerCompanyId("")
    }
  }, [showNetworkTabs, listScope])

  useEffect(() => {
    if (user?.role === "Agent" && showNetworkTabs) {
      loadPartnerCompanies()
    }
  }, [user, showNetworkTabs])

  useEffect(() => {
    fetchUsers()
    fetchLocationHubs()
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest(".menu-container")) {
        setOpenMenuId(null)
      }
      if (!e.target.closest(".sender-location-dropdown")) {
        setSenderLocationDropdownOpen(false)
      }
      if (!e.target.closest(".receiver-location-dropdown")) {
        setReceiverLocationDropdownOpen(false)
      }
      if (!e.target.closest(".payment-mode-dropdown")) {
        setPaymentModeDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [openMenuId])

  const normalizePaymentMode = (mode) =>
    String(mode || "").toLowerCase().replace(/\s+/g, "_")

  const buildSharedFilterParams = useCallback(() => {
    const params = {}
    if (filters.from_date) params.from_date = filters.from_date
    if (filters.to_date) params.to_date = filters.to_date
    if (filters.mode_of_payment?.length) {
      params.mode_of_payment = filters.mode_of_payment.map(normalizePaymentMode)
    }
    if (filters.sender_location?.length) params.sender_location = filters.sender_location
    if (filters.receiver_location?.length) params.receiver_location = filters.receiver_location
    if (filters.sender_location_id?.length) params.sender_location_id = filters.sender_location_id
    if (filters.receiver_location_id?.length) params.receiver_location_id = filters.receiver_location_id
    return params
  }, [
    filters.from_date,
    filters.to_date,
    filters.mode_of_payment,
    filters.sender_location,
    filters.receiver_location,
    filters.sender_location_id,
    filters.receiver_location_id,
  ])

  // Widget API supports dates + multi-select mode_of_payment + sender_location only
  const buildBookedByLocationParams = useCallback(() => {
    const params = {}
    if (filters.from_date) params.from_date = filters.from_date
    if (filters.to_date) params.to_date = filters.to_date
    if (filters.mode_of_payment?.length) {
      params.mode_of_payment = filters.mode_of_payment.map(normalizePaymentMode)
    }
    if (filters.sender_location?.length) params.sender_location = filters.sender_location
    return params
  }, [filters.from_date, filters.to_date, filters.mode_of_payment, filters.sender_location])

  const loadPartnerCompanies = async () => {
    try {
      const response = await agentPartnershipService.list({ status: "accepted" })
      const items = response.data?.data || []
      const unique = []
      const seen = new Set()
      items.forEach((p) => {
        if (!seen.has(p.requesting_company_id)) {
          seen.add(p.requesting_company_id)
          unique.push({
            id: p.requesting_company_id,
            name: p.requesting_company_name,
          })
        }
      })
      setPartnerCompanies(unique)
    } catch (error) {
      console.error("Failed to load partner companies", error)
    }
  }

  const buildFilterParams = useCallback((includePagination = true) => {
    const params = { ...buildSharedFilterParams() }
    if (includePagination) {
      params.page = currentPage
      params.per_page = 10
    }
    if (filters.status) params.status = filters.status
    if (filters.search) params.search = filters.search
    if (filters.user_id) params.user_id = filters.user_id
    if (filters.branch === "all") params.branch = "all"
    if (showNetworkTabs && listScope !== "all") params.scope = listScope
    if (partnerCompanyId && listScope === "network") params.partner_company_id = partnerCompanyId
    return params
  }, [buildSharedFilterParams, filters.status, filters.search, filters.user_id, filters.branch, currentPage, listScope, partnerCompanyId, showNetworkTabs])

  const buildBreakdownParams = useCallback(() => {
    const params = buildBookedByLocationParams()
    if (showNetworkTabs && listScope !== "all") params.scope = listScope
    if (partnerCompanyId && listScope === "network") params.partner_company_id = partnerCompanyId
    return params
  }, [buildBookedByLocationParams, showNetworkTabs, listScope, partnerCompanyId])

  const fetchBreakdown = useCallback(async () => {
    try {
      setBookedByLocationLoading(true)
      const params = buildBreakdownParams()
      const response = breakdownTab === "agent" && showNetworkTabs
        ? await shipmentService.getBookedByAgent(params)
        : await shipmentService.getBookedByLocation(params)
      setBookedByLocation(response.data)
    } catch (error) {
      console.error("Error fetching booked breakdown:", error)
    } finally {
      setBookedByLocationLoading(false)
    }
  }, [breakdownTab, showNetworkTabs, buildBreakdownParams])

  const fetchShipments = useCallback(async () => {
    try {
      setLoading(true)
      const params = buildFilterParams()
      const response = await shipmentService.getShipments(params)
      setShipments((prev) => mergeLoadedPage(currentPage, response.data.shipments || [], prev))
      setTotalPages(response.data.pagination?.pages || 1)
      if (canSeeReceiptPrintCount && response.data.receipt_print_count_total != null) {
        setReceiptPrintCountTotal(Number(response.data.receipt_print_count_total) || 0)
      } else {
        setReceiptPrintCountTotal(null)
      }
    } catch (error) {
      console.error("Error fetching shipments:", error)
      Alert.error(error.response?.data?.error || "Failed to load shipments")
      if (currentPage <= 1) setShipments([])
      setReceiptPrintCountTotal(null)
    } finally {
      setLoading(false)
    }
  }, [buildFilterParams, canSeeReceiptPrintCount])

  useEffect(() => {
    if (bookedByLocationOpen) {
      fetchBreakdown()
    }
  }, [bookedByLocationOpen, fetchBreakdown])

  usePaginatedListFetch({
    fetchPage: fetchShipments,
    setCurrentPage,
    currentPage,
    resetPageDeps: [
      filters.status,
      filters.search,
      filters.user_id,
      filters.branch,
      filters.from_date,
      filters.to_date,
      JSON.stringify(filters.mode_of_payment || []),
      JSON.stringify(filters.sender_location || []),
      JSON.stringify(filters.receiver_location || []),
      listScope,
      partnerCompanyId,
    ],
    debounceWhen: filters.search,
    debounceMs: 0,
  })

  const fetchUsers = async () => {
    try {
      const response = await shipmentService.getDistinctUsers()
      setUsers(response.data.users || [])
    } catch (error) {
      console.error("Error fetching users:", error)
    }
  }

  const fetchLocationHubs = async () => {
    try {
      const response = await locationHubService.getAllHubs(1, 500)
      const data = response.data
      const hubs = data.hubs || data.items || data.location_hubs || []
      setShipmentLocations(filterShipmentLocations(hubs))
    } catch (error) {
      console.error("Error fetching location hubs:", error)
    }
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters({ ...filters, [name]: value })
    setCurrentPage(1)
  }

  const handleClearFilters = () => {
    const todayStr = getLocalDateString()
    setFilters({
      status: "",
      search: "",
      from_date: todayStr,
      to_date: todayStr,
      mode_of_payment: [],
      user_id: "",
      branch: "",
      sender_location: [],
      receiver_location: [],
      sender_location_id: [],
      receiver_location_id: [],
    })
    setSenderLocationSearch("")
    setReceiverLocationSearch("")
    setSenderLocationDropdownOpen(false)
    setReceiverLocationDropdownOpen(false)
    setPaymentModeDropdownOpen(false)
    setCurrentPage(1)
  }

  const hasActiveFilters =
    filters.status ||
    filters.search ||
    filters.from_date !== todayStr ||
    filters.to_date !== todayStr ||
    (filters.mode_of_payment?.length > 0) ||
    filters.user_id ||
    filters.branch ||
    (filters.sender_location?.length > 0) ||
    (filters.receiver_location?.length > 0) ||
    (filters.sender_location_id?.length > 0) ||
    (filters.receiver_location_id?.length > 0)

  const getStatusColor = (status) => {
    const colors = {
      Booked: "bg-blue-100 text-blue-800 border-blue-200",
      "In Transit": "bg-indigo-100 text-indigo-800 border-indigo-200",
      Dispatched: "bg-purple-100 text-purple-800 border-purple-200",
      Received: "bg-yellow-100 text-yellow-800 border-yellow-200",
      Delivered: "bg-green-100 text-green-800 border-green-200",
      Returned: "bg-red-100 text-red-800 border-red-200",
      Rejected: "bg-gray-100 text-gray-500 border-gray-200",
      Cancelled: "bg-gray-100 text-gray-500 border-gray-200",
      "Cancel pending": "bg-amber-100 text-amber-800 border-amber-200",
    }
    return colors[status] || "bg-gray-100 text-gray-800 border-gray-200"
  }

  const toggleMultiSelect = (field, value) => {
    const normalizedValue = field === "mode_of_payment" ? normalizePaymentMode(value) : value
    setFilters((prev) => {
      const current = prev[field] || []
      const exists = current.some((v) => String(v) === String(normalizedValue))
      const updated = exists
        ? current.filter((v) => String(v) !== String(normalizedValue))
        : [...current, normalizedValue]
      return { ...prev, [field]: updated }
    })
    setCurrentPage(1)
  }

  const locationLabelById = (id) => {
    const loc = shipmentLocations.find((l) => String(l.id) === String(id))
    return loc ? formatHubBranchLabel(loc) : String(id)
  }

  const filteredShipmentLocations = (search) => {
    const q = (search || "").toLowerCase().trim()
    if (!q) return shipmentLocations
    return shipmentLocations.filter((loc) => {
      const label = formatHubBranchLabel(loc).toLowerCase()
      const name = (loc.location || "").toLowerCase()
      return label.includes(q) || name.includes(q)
    })
  }

  const filterTriggerClass =
    "w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl cursor-pointer flex justify-between items-center bg-white text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-400"

  const handleExportPDF = async () => {
    try {
      const exportParams = buildFilterParams(false)
      const response = await shipmentService.exportPDF(exportParams)
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `shipments_${new Date().toISOString().split("T")[0]}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      Alert.success("PDF exported successfully")
    } catch (error) {
      Alert.error(await parseBlobErrorMessage(error, "Failed to export PDF"))
    }
  }

  const handleBulkDownloadQR = async () => {
    try {
      setBulkQrDownloading(true)
      const params = buildFilterParams(false)
      const response = await shipmentService.bulkDownloadQRCodesFiltered(params)
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute(
        "download",
        `shipment_qr_codes_${new Date().toISOString().split("T")[0]}.pdf`
      )
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      Alert.success("QR codes PDF downloaded")
    } catch (error) {
      const message = await parseBlobErrorMessage(
        error,
        "Failed to download QR codes"
      )
      Alert.error(message)
    } finally {
      setBulkQrDownloading(false)
    }
  }

  const toggleMenu = (shipmentId) => {
    setOpenMenuId(openMenuId === shipmentId ? null : shipmentId)
  }

  const handleDownloadInvoice = async (shipment) => {
    try {
      setOpenMenuId(null)
      const response = await shipmentService.downloadInvoice(shipment.shipment_id)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `invoice_${shipment.shipment_id}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      Alert.success("Invoice downloaded successfully")
    } catch (error) {
      Alert.error("Failed to download invoice")
    }
  }

  const handlePrintEwayBill = async (shipment) => {
    try {
      setOpenMenuId(null)
      const response = await shipmentService.printEwayBill(shipment.shipment_id)
      const result = await printPdfBlob(response.data, `eway_bill_${shipment.shipment_id}.pdf`)
      if (result.method === 'download') {
        Alert.success('Popup blocked — e-Way Bill PDF downloaded. Open the file to print.')
      }
    } catch (error) {
      Alert.error(await parseBlobErrorMessage(error, "Failed to print e-Way Bill"))
    }
  }

  const handlePrintInvoice = async (shipment) => {
    try {
      setOpenMenuId(null)
      const response = await shipmentService.downloadInvoice(shipment.shipment_id)
      const result = await printPdfBlob(
        response.data,
        `invoice_${shipment.shipment_id}.pdf`
      )
      if (result.method === 'download') {
        Alert.success('Popup blocked — invoice PDF downloaded. Open the file to print.')
      }
    } catch (error) {
      Alert.error(await parseBlobErrorMessage(error, "Failed to print invoice"))
    }
  }

  const handleDownloadReceipt = async (shipment) => {
    try {
      setOpenMenuId(null)
      const response = await shipmentService.downloadReceipt(shipment.shipment_id)
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `receipt_${shipment.shipment_id}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      Alert.success("Receipt downloaded successfully")
    } catch (error) {
      Alert.error(await parseBlobErrorMessage(error, "Failed to download receipt"))
    }
  }

  const handlePrintReceipt = async (shipment) => {
    try {
      setOpenMenuId(null)
      const response = await shipmentService.printReceipt(shipment.shipment_id)
      const html = typeof response.data === "string" ? response.data : ""
      if (!openHtmlPrintWindow(html)) {
        Alert.error("Popup blocked — allow popups to print the receipt")
      }
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to print receipt")
    }
  }

  const handlePrintQR = async (shipment) => {
    try {
      setOpenMenuId(null)
      const response = await shipmentService.getQRCode(shipment.shipment_id)
      const result = await printPdfBlob(
        response.data,
        `QR_Code_${shipment.shipment_id}.pdf`
      )
      if (result.method === 'download') {
        Alert.success('Popup blocked — QR PDF downloaded. Open the file to print.')
      }
    } catch (error) {
      Alert.error(await parseBlobErrorMessage(error, "Failed to print QR code"))
    }
  }

  const openCancelModal = (shipment, mode) => {
    setOpenMenuId(null)
    setCancelModal({ open: true, mode, shipment })
  }

  const handleCancelSubmit = async (reason) => {
    const shipment = cancelModal.shipment
    if (!shipment) return
    try {
      setCancelSubmitting(true)
      if (cancelModal.mode === 'approve') {
        await shipmentService.approveCancel(shipment.shipment_id, reason)
        Alert.success('Shipment cancelled')
      } else if (cancelModal.mode === 'reject') {
        await shipmentService.rejectCancel(shipment.shipment_id, reason)
        Alert.success('Cancel request rejected')
      } else {
        const res = await shipmentService.requestCancel(shipment.shipment_id, reason)
        Alert.success(res.data?.message || 'Cancel submitted')
      }
      setCancelModal({ open: false, mode: 'request', shipment: null })
      fetchShipments()
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to update cancellation')
    } finally {
      setCancelSubmitting(false)
    }
  }

  const cancelMenuItems = (shipment) => {
    const items = []
    if (shipment.can_approve_cancel) {
      items.push({
        label: 'Approve cancel',
        icon: '✅',
        variant: 'success',
        onClick: () => openCancelModal(shipment, 'approve'),
      })
    }
    if (shipment.can_reject_cancel_request) {
      items.push({
        label: 'Reject request',
        icon: '↩️',
        onClick: () => openCancelModal(shipment, 'reject'),
      })
    }
    if ((shipment.can_request_cancel || shipment.can_cancel_now) && !shipment.can_approve_cancel) {
      items.push({
        label: 'Cancel',
        icon: '🚫',
        variant: 'danger',
        onClick: () => openCancelModal(shipment, 'request'),
      })
    }
    return items
  }

  const shipmentRowClass = (shipment, index) => {
    if (isCancelledShipment(shipment)) {
      return 'bg-gray-50 text-gray-400 hover:bg-gray-100 cursor-pointer'
    }
    if (isCancelPending(shipment)) {
      return 'bg-amber-50 hover:bg-amber-100'
    }
    return `hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`
  }

  const handleGcClick = (shipment) => {
    if (isCancelledShipment(shipment)) {
      openCancelModal(shipment, 'view')
      return
    }
    navigate(`/shipments/view/${shipment.shipment_id}`)
  }

  const extraActionItems = (shipment) => {
    if (isCancelledShipment(shipment)) return []
    return [
      ...cancelMenuItems(shipment),
      ...(hasShipmentCrossingAccess(user) && shipment.status === 'Dispatched'
        ? [{
            label: 'Unload at Crossing',
            icon: '📦',
            onClick: () => navigate(`/shipment-crossing?step=unload&shipment_id=${shipment.shipment_id}`),
          }]
        : []),
      ...(hasShipmentCrossingAccess(user) && shipment.status === 'Received'
        ? [{
            label: 'Dispatch after Crossing',
            icon: '🔀',
            onClick: () => navigate(`/shipment-crossing?step=dispatch&shipment_id=${shipment.shipment_id}`),
          }]
        : []),
      { label: 'Download Invoice', icon: '📄', onClick: () => handleDownloadInvoice(shipment) },
      { label: 'Print Invoice', icon: '🖨️', onClick: () => handlePrintInvoice(shipment) },
      { label: 'Download Receipt', icon: '🧾', onClick: () => handleDownloadReceipt(shipment) },
      { label: 'Print Receipt', icon: '🧾', onClick: () => handlePrintReceipt(shipment) },
      { label: 'Print QR Code', icon: '🔳', onClick: () => handlePrintQR(shipment) },
      ...((shipment.eway_bill_numbers || []).length
        ? [{ label: 'Print e-Way Bill', icon: '🖨️', onClick: () => handlePrintEwayBill(shipment) }]
        : []),
    ]
  }

  return (
    <DashboardLayout>
      <div className={listPageWrapperClass}>
                <ListPageHeader title="Shipments" icon={Package}>
                    {canSeeReceiptPrintCount && receiptPrintCountTotal != null && (
                      <span
                        className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-800 whitespace-nowrap"
                        title="Total receipt prints for current filters"
                      >
                        Receipt prints
                        <span className="font-bold tabular-nums">{receiptPrintCountTotal}</span>
                      </span>
                    )}
                    {user?.role !== "Accountant" && (
                      <Button
                        mobileIconOnly
                        variant="primary"
                        icon={Plus}
                        onClick={() => navigate("/shipments/add")}
                      >
                        Add Shipment
                      </Button>
                    )}
                    <Button
                      mobileIconOnly
                      variant="secondary"
                      icon={QrCode}
                      loading={bulkQrDownloading}
                      disabled={bulkQrDownloading}
                      onClick={handleBulkDownloadQR}
                    >
                      Download QR Codes
                    </Button>
                    {(user?.role === "System Admin" || user?.role === "Admin") && (
                      <Button
                        mobileIconOnly
                        variant="secondary"
                        icon={FileDown}
                        onClick={handleExportPDF}
                      >
                        Export PDF
                      </Button>
                    )}
                    <Button
                      mobileIconOnly
                      variant={bookedByLocationOpen ? "primary" : "secondary"}
                      icon={BarChart3}
                      onClick={() => setBookedByLocationOpen((open) => !open)}
                      mobileLabel={bookedByLocationOpen ? "Hide Breakdown" : "Booked Breakdown"}
                    >
                      {bookedByLocationOpen ? "Hide Breakdown" : "Booked Breakdown"}
                    </Button>
                </ListPageHeader>

                {showNetworkTabs && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {[
                      { id: "all", label: "All" },
                      { id: "company", label: "Company" },
                      { id: "network", label: "Network" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => { setListScope(tab.id); setCurrentPage(1); }}
                        className={listScopeTabClass(listScope === tab.id)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}

                {showNetworkTabs && user?.role === "Agent" && listScope === "network" && partnerCompanies.length > 0 && (
                  <div className="mb-4">
                    <select
                      className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                      value={partnerCompanyId}
                      onChange={(e) => { setPartnerCompanyId(e.target.value); setCurrentPage(1); }}
                    >
                      <option value="">All partner companies</option>
                      {partnerCompanies.map((company) => (
                        <option key={company.id} value={company.id}>{company.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Booked Shipments Breakdown — hidden by default, opened from header button */}
                {bookedByLocationOpen && (
                <div className="bg-white rounded-xl mb-4 shadow-md border border-blue-100 overflow-hidden">
                  <div className="px-4 py-4 bg-gradient-to-r from-blue-50 via-white to-white border-b border-blue-100">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <BarChart3 className="w-5 h-5 text-blue-500 shrink-0" />
                        <h2 className="font-semibold text-gray-800 text-sm sm:text-base">
                          Booked Shipments Breakdown
                        </h2>
                      </div>
                      <button
                        type="button"
                        onClick={() => setBookedByLocationOpen(false)}
                        className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 shrink-0"
                        aria-label="Close breakdown"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    {bookedByLocation?.summary && (
                      <div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap">
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-[11px] sm:text-xs font-bold border border-blue-200 text-center md:text-left">
                          {bookedByLocation.summary.total_booked_shipments} Booked
                        </span>
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-[11px] sm:text-xs font-bold border border-blue-200 text-center md:text-left">
                          ₹{bookedByLocation.summary.total_charges?.toLocaleString("en-IN")} Total
                        </span>
                        <span className="px-2 py-1 rounded-full bg-green-100 text-green-800 text-[11px] sm:text-xs font-bold border border-green-200 text-center md:text-left">
                          ₹{bookedByLocation.summary.total_paid?.toLocaleString("en-IN")} Paid
                        </span>
                        <span className="px-2 py-1 rounded-full bg-red-100 text-red-800 text-[11px] sm:text-xs font-bold border border-red-200 text-center md:text-left">
                          ₹{(bookedByLocation.summary.to_pay ?? bookedByLocation.summary.total_balance)?.toLocaleString("en-IN")} To Pay
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="px-4 pb-4">
                      {showNetworkTabs && (
                        <div className="flex flex-wrap gap-2 py-3 border-b border-gray-100">
                          {[
                            { id: "location", label: "By Sender Location" },
                            { id: "agent", label: "By Agent" },
                          ].map((tab) => (
                            <button
                              key={tab.id}
                              type="button"
                              onClick={() => setBreakdownTab(tab.id)}
                              className={listScopeTabSmClass(breakdownTab === tab.id)}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                      )}

                      {bookedByLocationLoading ? (
                        <div className="py-8 text-center text-sm text-gray-500">Loading breakdown…</div>
                      ) : bookedByLocation?.data?.length ? (
                        <>
                          {bookedByLocation?.summary?.payment_modes && (
                            <div className="grid grid-cols-2 gap-2 py-3 border-b border-gray-100 md:flex md:flex-wrap md:items-center">
                              <span className="text-xs text-gray-500 self-center col-span-2 md:col-auto md:mr-1">Filter by payment:</span>
                              {Object.entries(bookedByLocation.summary.payment_modes).map(([mode, count]) => {
                                const modeValue = normalizePaymentMode(mode)
                                return (
                                  <span
                                    key={mode}
                                    onClick={() => toggleMultiSelect("mode_of_payment", modeValue)}
                                    className={`px-2 sm:px-3 py-1 rounded-full text-[11px] sm:text-xs font-semibold border cursor-pointer transition-all text-center md:text-left ${
                                      filters.mode_of_payment?.includes(modeValue)
                                        ? "bg-buttonBg text-white border-buttonBg"
                                        : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-blue-50 hover:border-blue-200"
                                    }`}
                                  >
                                    {mode.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}: {count}
                                  </span>
                                )
                              })}
                            </div>
                          )}

                          {/* Mobile — location/agent cards */}
                          <div className="md:hidden space-y-2 pt-3">
                            {breakdownTab === "agent" && showNetworkTabs
                              ? bookedByLocation.data.map((row) => (
                                <div key={row.agent_user_id} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3">
                                  <p className="font-semibold text-gray-800 text-sm">{row.agent_name}</p>
                                  <p className="text-gray-500 text-xs mb-2">{row.agent_user_id}{row.agent_city ? ` · ${row.agent_city}` : ""}</p>
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div><span className="text-gray-500 block">Shipments</span><span className="font-semibold">{row.total_shipments}</span></div>
                                    <div><span className="text-gray-500 block">Total</span><span className="font-semibold">₹{row.total_charges?.toLocaleString("en-IN")}</span></div>
                                    <div><span className="text-gray-500 block">Paid</span><span className="font-semibold text-green-700">₹{row.total_paid?.toLocaleString("en-IN")}</span></div>
                                    <div><span className="text-gray-500 block">To Pay</span><span className="font-semibold text-red-700">₹{(row.to_pay ?? row.total_balance)?.toLocaleString("en-IN")}</span></div>
                                  </div>
                                </div>
                              ))
                              : bookedByLocation.data.map((loc) => (
                                <div
                                  key={loc.sender_location}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => {
                                    setFilters((f) => {
                                      const current = f.sender_location || []
                                      const isSelected = current.includes(loc.sender_location)
                                      return {
                                        ...f,
                                        sender_location: isSelected
                                          ? current.filter((v) => v !== loc.sender_location)
                                          : [...current, loc.sender_location],
                                        status: isSelected ? "" : "Booked",
                                      }
                                    })
                                    setCurrentPage(1)
                                  }}
                                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.currentTarget.click() }}
                                  className={`rounded-lg border p-3 cursor-pointer transition-colors ${
                                    filters.sender_location?.includes(loc.sender_location)
                                      ? "border-blue-300 bg-blue-50"
                                      : "border-gray-100 bg-gray-50/60 hover:bg-blue-50/60"
                                  }`}
                                >
                                  <p className="font-semibold text-gray-800 text-sm uppercase">{loc.sender_location}</p>
                                  <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                                    <div><span className="text-gray-500 block">Shipments</span><span className="font-semibold">{loc.total_shipments}</span></div>
                                    <div><span className="text-gray-500 block">Total</span><span className="font-semibold">₹{loc.total_charges?.toLocaleString("en-IN")}</span></div>
                                    <div><span className="text-gray-500 block">Paid</span><span className="font-semibold text-green-700">₹{loc.total_paid?.toLocaleString("en-IN")}</span></div>
                                    <div><span className="text-gray-500 block">To Pay</span><span className="font-semibold text-red-700">₹{(loc.to_pay ?? loc.total_balance)?.toLocaleString("en-IN")}</span></div>
                                  </div>
                                </div>
                              ))}
                            {bookedByLocation.summary && (
                              <div className="rounded-lg border-2 border-blue-200 bg-blue-50 p-3 font-bold text-xs sm:text-sm">
                                <p className="mb-2">Grand Total</p>
                                <div className="grid grid-cols-2 gap-2 font-semibold">
                                  <div><span className="text-gray-600 block font-medium">Shipments</span>{bookedByLocation.summary.total_booked_shipments}</div>
                                  <div><span className="text-gray-600 block font-medium">Total</span>₹{bookedByLocation.summary.total_charges?.toLocaleString("en-IN")}</div>
                                  <div><span className="text-gray-600 block font-medium">Paid</span><span className="text-green-800">₹{bookedByLocation.summary.total_paid?.toLocaleString("en-IN")}</span></div>
                                  <div><span className="text-gray-600 block font-medium">To Pay</span><span className="text-red-800">₹{(bookedByLocation.summary.to_pay ?? bookedByLocation.summary.total_balance)?.toLocaleString("en-IN")}</span></div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Tablet / desktop — table */}
                          <div className="hidden md:block overflow-x-auto pt-3">
                            <table className="min-w-full text-xs sm:text-sm">
                              <thead>
                                <tr className="border-b bg-gray-50 text-left text-gray-600">
                                  <th className="py-2.5 px-3 font-semibold">{breakdownTab === "agent" && showNetworkTabs ? "Agent" : "Sender Location"}</th>
                                  <th className="py-2.5 px-3 text-right font-semibold">{t('pages.shipment.title')}</th>
                                  <th className="py-2.5 px-3 text-right font-semibold">Total</th>
                                  <th className="py-2.5 px-3 text-right font-semibold">Paid</th>
                                  <th className="py-2.5 px-3 text-right font-semibold">To Pay</th>
                                </tr>
                              </thead>
                              <tbody>
                                {breakdownTab === "agent" && showNetworkTabs
                                  ? bookedByLocation.data.map((row) => (
                                    <tr key={row.agent_user_id} className="border-b hover:bg-blue-50/60">
                                      <td className="py-2.5 px-3">
                                        <div className="font-semibold text-gray-800">{row.agent_name}</div>
                                        <div className="text-gray-500 text-xs">{row.agent_user_id}{row.agent_city ? ` · ${row.agent_city}` : ""}</div>
                                      </td>
                                      <td className="py-2.5 px-3 text-right">{row.total_shipments}</td>
                                      <td className="py-2.5 px-3 text-right">₹{row.total_charges?.toLocaleString("en-IN")}</td>
                                      <td className="py-2.5 px-3 text-right text-green-700">₹{row.total_paid?.toLocaleString("en-IN")}</td>
                                      <td className="py-2.5 px-3 text-right text-red-700">₹{(row.to_pay ?? row.total_balance)?.toLocaleString("en-IN")}</td>
                                    </tr>
                                  ))
                                  : bookedByLocation.data.map((loc) => (
                                    <tr
                                      key={loc.sender_location}
                                      className={`border-b cursor-pointer hover:bg-blue-50/60 ${filters.sender_location?.includes(loc.sender_location) ? "bg-blue-100/80" : ""}`}
                                      onClick={() => {
                                        setFilters((f) => {
                                          const current = f.sender_location || []
                                          const isSelected = current.includes(loc.sender_location)
                                          return {
                                            ...f,
                                            sender_location: isSelected
                                              ? current.filter((v) => v !== loc.sender_location)
                                              : [...current, loc.sender_location],
                                            status: isSelected ? "" : "Booked",
                                          }
                                        })
                                        setCurrentPage(1)
                                      }}
                                    >
                                      <td className="py-2.5 px-3 font-semibold uppercase">{loc.sender_location}</td>
                                      <td className="py-2.5 px-3 text-right">{loc.total_shipments}</td>
                                      <td className="py-2.5 px-3 text-right">₹{loc.total_charges?.toLocaleString("en-IN")}</td>
                                      <td className="py-2.5 px-3 text-right text-green-700">₹{loc.total_paid?.toLocaleString("en-IN")}</td>
                                      <td className="py-2.5 px-3 text-right text-red-700">₹{(loc.to_pay ?? loc.total_balance)?.toLocaleString("en-IN")}</td>
                                    </tr>
                                  ))}
                                {bookedByLocation.summary && (
                                  <tr className="bg-blue-50 font-bold border-t-2 border-blue-200">
                                    <td className="py-2.5 px-3">Grand Total</td>
                                    <td className="py-2.5 px-3 text-right">{bookedByLocation.summary.total_booked_shipments}</td>
                                    <td className="py-2.5 px-3 text-right">₹{bookedByLocation.summary.total_charges?.toLocaleString("en-IN")}</td>
                                    <td className="py-2.5 px-3 text-right text-green-800">₹{bookedByLocation.summary.total_paid?.toLocaleString("en-IN")}</td>
                                    <td className="py-2.5 px-3 text-right text-red-800">₹{(bookedByLocation.summary.to_pay ?? bookedByLocation.summary.total_balance)?.toLocaleString("en-IN")}</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          <p className="text-xs text-gray-400 mt-3">
                            {breakdownTab === "location" ? "Click a sender location row to filter the shipment list below." : "Breakdown by booking agent for the selected date range."}
                          </p>
                        </>
                      ) : (
                        <div className="py-8 text-center text-sm text-gray-500">No booked shipments for this period</div>
                      )}
                  </div>
                </div>
                )}

                <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={handleClearFilters}>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">{t('common.search')}</label>
                      <SearchSubmitInput
                        name="search"
                        value={filters.search}
                        onSearch={(value) => setFilters((prev) => ({ ...prev, search: value }))}
                        placeholder="GC, CN, consigner, consignee..."
                        inputClassName="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status</label>
                      <select
                        name="status"
                        value={filters.status}
                        onChange={handleFilterChange}
                        className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                      >
                        <option value="">All Statuses</option>
                        <option value="Booked">Booked</option>
                        <option value="In Transit">In Transit</option>
                        <option value="Dispatched">Dispatched</option>
                        <option value="Received">Received</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Returned">Returned</option>
                        <option value="Rejected">Cancelled</option>
                      </select>
                    </div>
                    <div className="relative payment-mode-dropdown">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        Payment Mode
                      </label>
                      <div
                        onClick={() => {
                          setPaymentModeDropdownOpen((o) => !o)
                          setSenderLocationDropdownOpen(false)
                          setReceiverLocationDropdownOpen(false)
                        }}
                        className={filterTriggerClass}
                      >
                        <span className={`min-w-0 flex-1 truncate pr-2 ${filters.mode_of_payment?.length ? "text-gray-800" : "text-gray-400"}`}>
                          {filters.mode_of_payment?.length
                            ? filters.mode_of_payment.map((m) => m.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())).join(", ")
                            : "All Payment Modes"}
                        </span>
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${paymentModeDropdownOpen ? "rotate-180" : ""}`}
                          aria-hidden="true"
                        />
                      </div>
                      {paymentModeDropdownOpen && (
                        <div
                          className="absolute z-50 top-full left-0 w-full bg-white border border-gray-300 rounded-xl shadow-lg mt-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="max-h-48 overflow-y-auto">
                            {filters.mode_of_payment?.length > 0 && (
                              <div
                                onClick={() => {
                                  setFilters((f) => ({ ...f, mode_of_payment: [] }))
                                  setCurrentPage(1)
                                }}
                                className="px-3 py-2 text-xs text-blue-600 font-semibold hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                              >
                                Clear selection
                              </div>
                            )}
                            {[
                              { value: "to_pay", label: "To Pay" },
                              { value: "credit", label: "Credit" },
                              { value: "paid", label: "Paid" },
                              { value: "paid_balance", label: "Paid Balance" },
                            ].map(({ value, label }) => (
                              <label key={value} className="flex items-center gap-2 px-3 sm:px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm sm:text-base text-gray-800">
                                <input
                                  type="checkbox"
                                  checked={filters.mode_of_payment?.includes(value)}
                                  onChange={() => toggleMultiSelect("mode_of_payment", value)}
                                  className="accent-blue-500"
                                />
                                {label}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {user?.role === "Agent" && (
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Branch</label>
                        <select
                          name="branch"
                          value={filters.branch}
                          onChange={handleFilterChange}
                          className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          <option value="">My shipments</option>
                          <option value="all">Whole branch</option>
                        </select>
                      </div>
                    )}
                    {(user?.role === "System Admin" || user?.role === "Admin" || user?.role === "Accountant") && (
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">User</label>
                        <select
                          name="user_id"
                          value={filters.user_id}
                          onChange={handleFilterChange}
                          className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          <option value="">All Users</option>
                          {users.map((u) => (
                            <option key={u.user_id} value={u.user_id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">From Date</label>
                        <input
                          type="date"
                          name="from_date"
                          value={filters.from_date}
                          onChange={handleFilterChange}
                          className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">To Date</label>
                        <input
                          type="date"
                          name="to_date"
                          value={filters.to_date}
                          onChange={handleFilterChange}
                          className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>

                      <div className="relative sender-location-dropdown">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Sender Location</label>
                        <div
                          onClick={() => {
                            setSenderLocationDropdownOpen((o) => !o)
                            setReceiverLocationDropdownOpen(false)
                            setPaymentModeDropdownOpen(false)
                          }}
                          className={filterTriggerClass}
                        >
                          <span className={`min-w-0 flex-1 truncate pr-2 ${filters.sender_location_id?.length ? "text-gray-800" : "text-gray-400"}`}>
                            {filters.sender_location_id?.length
                              ? filters.sender_location_id.map(locationLabelById).join(", ")
                              : "All Sender Locations"}
                          </span>
                          <ChevronDown className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${senderLocationDropdownOpen ? "rotate-180" : ""}`} />
                        </div>
                        {senderLocationDropdownOpen && (
                          <div className="absolute z-50 top-full left-0 w-full bg-white border border-gray-300 rounded-xl shadow-lg mt-1">
                            <div className="p-2 border-b border-gray-200">
                              <input
                                type="text"
                                placeholder="Search location..."
                                value={senderLocationSearch}
                                onChange={(e) => setSenderLocationSearch(e.target.value)}
                                autoFocus
                                className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
                              />
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              {filters.sender_location_id?.length > 0 && (
                                <div
                                  onClick={() => {
                                    setFilters((f) => ({ ...f, sender_location_id: [] }))
                                    setSenderLocationSearch("")
                                    setCurrentPage(1)
                                  }}
                                  className="px-3 py-2 text-xs text-blue-600 font-semibold hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                                >
                                  Clear selection
                                </div>
                              )}
                              {filteredShipmentLocations(senderLocationSearch).map((loc) => (
                                <label key={loc.id} className="flex items-center gap-2 px-3 sm:px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm sm:text-base text-gray-800">
                                  <input
                                    type="checkbox"
                                    checked={filters.sender_location_id?.some((id) => String(id) === String(loc.id))}
                                    onChange={() => toggleMultiSelect("sender_location_id", loc.id)}
                                    className="accent-blue-500"
                                  />
                                  {formatHubBranchLabel(loc)}
                                </label>
                              ))}
                              {filteredShipmentLocations(senderLocationSearch).length === 0 && (
                                <div className="px-3 py-2 text-sm text-gray-400">No locations found</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="relative receiver-location-dropdown">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Receiver Location</label>
                        <div
                          onClick={() => {
                            setReceiverLocationDropdownOpen((o) => !o)
                            setSenderLocationDropdownOpen(false)
                            setPaymentModeDropdownOpen(false)
                          }}
                          className={filterTriggerClass}
                        >
                          <span className={`min-w-0 flex-1 truncate pr-2 ${filters.receiver_location_id?.length ? "text-gray-800" : "text-gray-400"}`}>
                            {filters.receiver_location_id?.length
                              ? filters.receiver_location_id.map(locationLabelById).join(", ")
                              : "All Receiver Locations"}
                          </span>
                          <ChevronDown className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${receiverLocationDropdownOpen ? "rotate-180" : ""}`} />
                        </div>
                        {receiverLocationDropdownOpen && (
                          <div className="absolute z-50 top-full left-0 w-full bg-white border border-gray-300 rounded-xl shadow-lg mt-1">
                            <div className="p-2 border-b border-gray-200">
                              <input
                                type="text"
                                placeholder="Search location..."
                                value={receiverLocationSearch}
                                onChange={(e) => setReceiverLocationSearch(e.target.value)}
                                autoFocus
                                className="w-full px-3 sm:px-4 py-1.5 border border-gray-300 rounded-xl text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
                              />
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              {filters.receiver_location_id?.length > 0 && (
                                <div
                                  onClick={() => {
                                    setFilters((f) => ({ ...f, receiver_location_id: [] }))
                                    setReceiverLocationSearch("")
                                    setCurrentPage(1)
                                  }}
                                  className="px-3 py-2 text-xs text-blue-600 font-semibold hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                                >
                                  Clear selection
                                </div>
                              )}
                              {filteredShipmentLocations(receiverLocationSearch).map((loc) => (
                                <label key={loc.id} className="flex items-center gap-2 px-3 sm:px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm sm:text-base text-gray-800">
                                  <input
                                    type="checkbox"
                                    checked={filters.receiver_location_id?.some((id) => String(id) === String(loc.id))}
                                    onChange={() => toggleMultiSelect("receiver_location_id", loc.id)}
                                    className="accent-blue-500"
                                  />
                                  {formatHubBranchLabel(loc)}
                                </label>
                              ))}
                              {filteredShipmentLocations(receiverLocationSearch).length === 0 && (
                                <div className="px-3 py-2 text-sm text-gray-400">No locations found</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                </ListFilterPanel>

                {/* Shipments Table - Desktop */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 w-full min-w-0 max-w-full">
                  {loading && currentPage === 1 ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader fullScreen={false} size="default" message="Loading shipments..." />
                    </div>
                  ) : shipments.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="text-6xl mb-4">🔭</div>
                      <p className="text-gray-600 text-lg font-semibold">No shipments found</p>
                      <p className="text-gray-500 mt-2">Try adjusting your filters or create a new shipment</p>
                    </div>
                  ) : (
                    <>
                      <div className="hidden xl:block w-full min-w-0">
                        <table className="w-full table-fixed border-collapse [&_th]:min-w-0 [&_td]:min-w-0">
                          <thead className="bg-gray-50 rounded-t-xl">
                            <tr className="border-b border-gray-200">
                              <th className="w-[14%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                                GC Number
                              </th>
                              <th className="w-[12%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Consigner</th>
                              <th className="w-[12%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Consignee</th>
                              <th className="w-[9%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Payment Mode</th>
                              <th className="w-[7%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                              <th className="w-[5%] px-3 2xl:px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Qty</th>
                              <th className="w-[6%] px-3 2xl:px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Weight</th>
                              <th className="w-[9%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Amount</th>
                              <th className="w-[9%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Balance</th>
                              <th className="w-[9%] px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Date</th>
                              <th className="w-[6%] px-3 2xl:px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">{t('common.actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {shipments.map((shipment, index) => (
                              <tr
                                key={shipment.id}
                                className={`relative transition-colors ${shipmentRowClass(shipment, index)}`}
                                onClick={() => {
                                  if (isCancelledShipment(shipment)) openCancelModal(shipment, 'view')
                                }}
                              >
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleGcClick(shipment)
                                    }}
                                    title={shipment.shipment_id}
                                    className={`font-regular hover:underline cursor-pointer break-words ${isCancelledShipment(shipment) ? 'text-gray-400' : 'text-secondary hover:text-secondary'}`}
                                  >
                                    {shipment.shipment_id}
                                  </span>
                                  {shipment.consignment_no && (
                                    <div className="text-[11px] text-gray-900 font-medium mt-0.5 break-words">
                                      CN: {shipment.consignment_no}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                                  <div className="font-regular text-gray-900 whitespace-normal break-words">
                                    {shipment.consigner_name || shipment.sender_name}
                                  </div>
                                  <div className="font-regular text-gray-900 whitespace-normal break-words">
                                    {shipment.consigner_contact || shipment.sender_contact}
                                  </div>
                                  {shipment.sender_location && (
                                    <div className="text-xs text-gray-400 mt-0.5 whitespace-normal break-words">📍 {shipment.sender_location}</div>
                                  )}
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                                  <div className="font-regular text-gray-900 whitespace-normal break-words">
                                    {shipment.consignee_name || shipment.receiver_name}
                                  </div>
                                  <div className="font-regular text-gray-900 whitespace-normal break-words">
                                    {shipment.consignee_contact || shipment.receiver_contact}
                                  </div>
                                  {shipment.receiver_location && (
                                    <div className="text-xs text-gray-400 mt-0.5 whitespace-normal break-words">📍 {shipment.receiver_location}</div>
                                  )}
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                                  <span className="font-regular text-gray-900 uppercase whitespace-nowrap">
                                    {shipment.mode_of_payment ? shipment.mode_of_payment.replace(/_/g, ' ') : 'N/A'}
                                  </span>
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top">
                                  <span
                                    className={`px-0 py-1 rounded-full text-sm font-regular uppercase whitespace-nowrap ${isCancelledShipment(shipment) || isCancelPending(shipment) ? getStatusColor(displayShipmentStatus(shipment)) : 'text-gray-900'}`}
                                  >
                                    {displayShipmentStatus(shipment)}
                                  </span>
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top text-right whitespace-nowrap">
                                  <span className="font-regular text-gray-900 tabular-nums">
                                    {formatListQuantity(shipment)}
                                  </span>
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top text-right whitespace-nowrap">
                                  <span className="font-regular text-gray-900 tabular-nums">
                                    {formatListWeight(shipment)}
                                  </span>
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top whitespace-nowrap">
                                  <span className="font-regular text-gray-900">
                                    ₹{shipment.total_amount?.toFixed(2)}
                                  </span>
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top whitespace-nowrap">
                                  <span className="font-regular text-base text-gray-900">
                                    ₹{shipment.balance_amount?.toFixed(2)}
                                  </span>
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm align-top text-gray-900 leading-5">
                                  <div className="whitespace-nowrap">{formatIndianDate(shipment.created_at)}</div>
                                  <div className="whitespace-nowrap">{formatIndianTime(shipment.created_at)}</div>
                                  {formatTimeAgo(shipment.created_at) ? (
                                    <div className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">{formatTimeAgo(shipment.created_at)}</div>
                                  ) : null}
                                </td>
                                <td className="px-3 2xl:px-4 py-3 text-sm text-center align-top" onClick={(e) => e.stopPropagation()}>
                                  <RowActionsMenu
                                    className="relative menu-container"
                                    isOpen={openMenuId === shipment.shipment_id}
                                    onToggle={() => toggleMenu(shipment.shipment_id)}
                                    onClose={() => setOpenMenuId(null)}
                                    onView={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                                    onEdit={canEditShipmentRow(shipment, user) ? () => navigate(`/shipments/edit/${shipment.shipment_id}`) : undefined}
                                    extraItems={extraActionItems(shipment)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Tablet & Mobile cards (inside same panel) */}
                      <div className="xl:hidden space-y-3 p-4">
                        {shipments.map((shipment) => (
                          <div
                            key={`card-${shipment.id}`}
                            className={`rounded-xl shadow-sm border p-4 border-l-4 ${
                              isCancelledShipment(shipment)
                                ? 'bg-gray-50 border-gray-100 border-l-gray-300 text-gray-400 cursor-pointer'
                                : isCancelPending(shipment)
                                  ? 'bg-amber-50 border-amber-100 border-l-amber-400'
                                  : 'bg-white border-gray-100 border-l-blue-500'
                            }`}
                            onClick={() => {
                              if (isCancelledShipment(shipment)) openCancelModal(shipment, 'view')
                            }}
                          >
                            <div className="mb-3 space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleGcClick(shipment)
                                    }}
                                    title={`#${shipment.shipment_id}`}
                                    className={`${listIdLinkMobileClass} block truncate`}
                                  >
                                    #{shipment.shipment_id}
                                  </span>
                                  {shipment.consignment_no && (
                                    <p className="text-[11px] text-gray-900 font-medium mt-0.5">CN: {shipment.consignment_no}</p>
                                  )}
                                  <p className="text-xs text-gray-400 mt-0.5 leading-4">
                                    {formatIndianDate(shipment.created_at)}
                                    {formatIndianTime(shipment.created_at) ? ` · ${formatIndianTime(shipment.created_at)}` : ''}
                                    {formatTimeAgo(shipment.created_at) ? ` · ${formatTimeAgo(shipment.created_at)}` : ''}
                                  </p>
                                </div>
                                <div onClick={(e) => e.stopPropagation()}>
                                <RowActionsMenu
                                  className="relative menu-container shrink-0"
                                  isOpen={openMenuId === `card-${shipment.shipment_id}`}
                                  onToggle={() => toggleMenu(`card-${shipment.shipment_id}`)}
                                  onClose={() => setOpenMenuId(null)}
                                  onView={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                                  onEdit={canEditShipmentRow(shipment, user) ? () => navigate(`/shipments/edit/${shipment.shipment_id}`) : undefined}
                                  extraItems={extraActionItems(shipment)}
                                />
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 uppercase">
                                  {shipment.mode_of_payment ? shipment.mode_of_payment.replace(/_/g, ' ') : 'N/A'}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${getStatusColor(displayShipmentStatus(shipment))}`}>
                                  {displayShipmentStatus(shipment)}
                                </span>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="min-w-0">
                                <p className="text-xs text-gray-500 font-medium">Consigner</p>
                                <p className="font-semibold text-gray-800 break-words">{shipment.consigner_name || shipment.sender_name}</p>
                                {shipment.sender_location && (
                                  <p className="text-[11px] text-gray-400 break-words">📍 {shipment.sender_location}</p>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-xs text-gray-500 font-medium">Consignee</p>
                                <p className="font-semibold text-gray-800 break-words">{shipment.consignee_name || shipment.receiver_name}</p>
                                {shipment.receiver_location && (
                                  <p className="text-[11px] text-gray-400 break-words">📍 {shipment.receiver_location}</p>
                                )}
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 font-medium">Qty</p>
                                <p className="font-bold text-gray-900 tabular-nums">{formatListQuantity(shipment)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 font-medium">Weight</p>
                                <p className="font-bold text-gray-900 tabular-nums">{formatListWeight(shipment)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 font-medium">Amount</p>
                                <p className="font-bold text-gray-900">₹{shipment.total_amount?.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 font-medium">Balance</p>
                                <p className={`font-bold ${shipment.balance_amount > 0 ? "text-red-600" : "text-green-600"}`}>
                                  ₹{shipment.balance_amount?.toFixed(2)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}

                      </div>

                      <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        loading={loading}
                        onNext={() => setCurrentPage((page) => page + 1)}
                      />
                    </>
                  )}
                </div>
      </div>
      <CancelShipmentModal
        open={cancelModal.open}
        mode={cancelModal.mode}
        shipment={cancelModal.shipment}
        submitting={cancelSubmitting}
        onClose={() => setCancelModal({ open: false, mode: 'request', shipment: null })}
        onSubmit={handleCancelSubmit}
      />
    </DashboardLayout>
  )
}

export default ShipmentList;